import styled, { css, keyframes } from "styled-components";
import { TextField as Input } from "@mui/material";
import color from "../../constants/color";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ========== Layout ==========

export const Content = styled.div`
  padding: 18px 24px 24px;
  margin-top: 66px;
  width: 100%;
  height: calc(100vh - 66px);
  display: flex;
  flex-direction: column;
  background-color: #f5f7fb;
  gap: 12px;

  @media (max-width: 900px) {
    padding: 12px;
    height: calc(100vh - 66px);
  }
`;

export const PageTitle = styled.h1`
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 24px;
  line-height: 29px;
  color: ${color.almostBlack};
  margin-bottom: 16px;
  letter-spacing: -0.01em;
`;

export const MainContainer = styled.div`
  display: flex;
  flex: 1;
  border-radius: 12px;
  overflow: hidden;
  background: ${color.white};
  box-shadow: 0 6px 24px rgba(16, 24, 40, 0.08);
  border: 1px solid #e8edf5;
  min-height: 0;
`;

export const TopTabsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const TopTabsGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const TopTabButton = styled.button<{ active?: boolean }>`
  position: relative;
  border: 1px solid ${(props) => (props.active ? "#d8e4ff" : "#e8edf5")};
  background: ${(props) => (props.active ? "#eef3ff" : "#f8fafc")};
  color: ${(props) => (props.active ? color.primary : color.gray500)};
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  border-radius: 10px;
  padding: 10px 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #d8e4ff;
    color: ${color.primary};
  }
`;

export const FloatingBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  background: #25d366;
  color: ${color.white};
  font-size: 10px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  line-height: 1;
  pointer-events: none;
  box-shadow: 0 0 0 2px #fff;
`;

export const EnviosHojePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border-radius: 10px;
  border: 1px solid #e8edf5;
  background: ${color.white};
  font-family: "Inter", sans-serif;
  font-size: 13px;
  line-height: 1;
`;

export const EnviosHojeDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f97316;
  flex-shrink: 0;
`;

export const EnviosHojeValue = styled.span`
  font-weight: 700;
  color: #1e293b;
`;

export const EnviosHojeMuted = styled.span`
  font-weight: 500;
  color: #94a3b8;
`;

export const PanelCard = styled.div`
  flex: 1;
  min-height: 0;
  border-radius: 12px;
  background: ${color.white};
  border: 1px solid #e8edf5;
  box-shadow: 0 6px 24px rgba(16, 24, 40, 0.08);
  overflow: hidden;
`;

// ========== Sidebar ==========

export const Sidebar = styled.div<{ hidden?: boolean }>`
  width: 380px;
  min-width: 380px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e2e8f0;
  background: ${color.white};

  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
    ${(props) =>
      props.hidden &&
      css`
        display: none;
      `}
  }
`;

export const SidebarHeader = styled.div`
  padding: 14px 16px 10px;
  background: #f0f2f5;
`;

export const SidebarTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

export const SidebarTitle = styled.h2`
  font-family: "Inter", sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #111b21;
  margin: 0;
`;

export const SidebarTopActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const HeaderIconBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: ${color.primary};
    border-color: #dbeafe;
    background: #f0f6ff;
  }
`;

export const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${color.white};
  border-radius: 8px;
  padding: 7px 12px;
  transition: box-shadow 0.2s;

  &:focus-within {
    box-shadow: 0 0 0 2px rgba(0, 128, 105, 0.15);
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 13px;
  font-family: "Inter", sans-serif;
  color: #334155;
  background: transparent;

  &::placeholder {
    color: #94a3b8;
  }
`;

export const ClearButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: #94a3b8;
  font-size: 14px;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #64748b;
  }
`;

export const FilterChips = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 10px;
  overflow: auto;
  padding-bottom: 2px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const FilterChip = styled.button<{ active?: boolean }>`
  border: none;
  border-radius: 999px;
  padding: 5px 12px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  color: ${(props) => (props.active ? "#008069" : "#54656f")};
  background: ${(props) => (props.active ? "#e7fce9" : "#ffffff")};
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${(props) => (props.active ? "#e7fce9" : "#f0f2f5")};
  }
`;

export const StatusCard = styled.div`
  margin-top: 10px;
  border-radius: 10px;
  background: #dff0d6;
  color: #315243;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  line-height: 1.45;
  padding: 10px 11px;

  strong {
    color: #128c47;
    font-weight: 700;
  }
`;

export const ArchivedRow = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #334155;
`;

export const ArchivedCount = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #0ea5a2;
`;

export const SidebarActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

export const ContatosButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid ${color.Primaryv3};
  color: ${color.primary};
  font-size: 13px;
  font-weight: 600;
  font-family: "Inter", sans-serif;
  padding: 8px;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${color.Primary50};
  }
`;

export const NovoContatoBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  color: ${color.white};
  background: #25d366;
  font-size: 12px;
  font-weight: 600;
  font-family: "Inter", sans-serif;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.2s;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }
`;

export const EnvioMassaBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid #25d366;
  color: #25d366;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  font-family: "Inter", sans-serif;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: rgba(37, 211, 102, 0.08);
  }
`;

export const ContactList = styled.div`
  overflow-y: auto;
  flex: 1;
  background: #ffffff;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d9e7;
    border-radius: 4px;
  }
`;

export const ContactItem = styled.button<{ active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 10px 16px;
  border: none;
  background: ${(props) => (props.active ? "#f0f2f5" : "transparent")};
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;

  &:hover {
    background: #f0f2f5;
  }
`;

export const AvatarWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

export const Avatar = styled.div<{ bgColor: string; size?: number }>`
  width: ${(props) => props.size || 44}px;
  height: ${(props) => props.size || 44}px;
  min-width: ${(props) => props.size || 44}px;
  border-radius: 50%;
  background: ${(props) => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${color.white};
  font-weight: 700;
  font-size: ${(props) => (props.size || 44) * 0.32}px;
  font-family: "Inter", sans-serif;
  position: relative;
  overflow: hidden;
`;

export const AvatarImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

export const UnreadBadge = styled.span`
  background: #25d366;
  color: ${color.white};
  font-size: 11px;
  font-weight: 600;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  line-height: 1;
  flex-shrink: 0;
  margin-left: auto;
`;

export const ContactInfo = styled.div`
  flex: 1;
  min-width: 0;
  border-bottom: 1px solid #e9edef;
  padding-bottom: 10px;
  padding-top: 2px;
`;

export const ContactRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const ContactName = styled.span<{ bold?: boolean }>`
  font-family: "Inter", sans-serif;
  font-weight: ${(props) => (props.bold ? 600 : 400)};
  font-size: 16px;
  color: #111b21;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ContactTime = styled.span<{ highlight?: boolean }>`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: ${(props) => (props.highlight ? "#25d366" : "#667781")};
  font-weight: 400;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const ContactPreviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 3px;
`;

export const ContactPreview = styled.p<{ bold?: boolean }>`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: ${(props) => (props.bold ? "#111b21" : "#667781")};
  font-weight: ${(props) => (props.bold ? 500 : 400)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
  flex: 1;
  min-width: 0;
`;

export const CheckMark = styled.span`
  font-size: 11px;
  color: #94a3b8;
`;

export const CheckMarkInline = styled.span<{ lida?: boolean }>`
  font-size: 12px;
  color: ${(props) => (props.lida ? "#53bdeb" : "#667781")};
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
`;

export const EmptyStateTitle = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${color.grey4};
  font-weight: 500;
`;

export const EmptyStateSubtitle = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: ${color.gray300};
  margin-top: 4px;
`;

// ========== Chat Panel ==========

export const ChatPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #f8fafc;
`;

export const ChatHeader = styled.div`
  background: #f0f2f5;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
`;

export const BackButton = styled.button`
  display: none;
  border: none;
  background: none;
  cursor: pointer;
  color: ${color.gray500};
  padding: 4px;

  &:hover {
    color: ${color.primary};
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
  }
`;

export const ChatHeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ChatHeaderName = styled.div`
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #111b21;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ChatHeaderSub = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: #667781;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const OnlineDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  animation: ${pulse} 2s infinite;
  flex-shrink: 0;
`;

// ========== Messages ==========

export const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background-color: #ebe5dd;
  background-image: radial-gradient(
      circle at 25px 25px,
      rgba(255, 255, 255, 0.15) 2px,
      transparent 2px
    ),
    radial-gradient(
      circle at 75px 75px,
      rgba(255, 255, 255, 0.15) 2px,
      transparent 2px
    );
  background-size: 100px 100px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 3px;
  }
`;

export const DateSeparator = styled.div`
  display: flex;
  justify-content: center;
  margin: 12px 0;
`;

export const DateLabel = styled.span`
  background: rgba(255, 255, 255, 0.9);
  color: #64748b;
  font-family: "Inter", sans-serif;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 14px;
  border-radius: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  user-select: none;
`;

export const MsgRow = styled.div<{ saida?: boolean }>`
  display: flex;
  justify-content: ${(props) => (props.saida ? "flex-end" : "flex-start")};
`;

export const MsgBubble = styled.div<{
  saida?: boolean;
  pending?: boolean;
  editingTarget?: boolean;
}>`
  max-width: 460px;
  border-radius: 12px;
  ${(props) =>
    props.saida
      ? css`
          background: #dcf8c6;
          border-top-right-radius: 4px;
          margin-left: 72px;
        `
      : css`
          background: ${color.white};
          border-top-left-radius: 4px;
          margin-right: 72px;
        `}
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  ${(props) =>
    props.pending &&
    css`
      opacity: 0.7;
    `}
  ${(props) =>
    props.editingTarget &&
    css`
      outline: 2px solid #22c55e;
      box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
    `}
`;

export const MsgMedia = styled.div`
  cursor: pointer;

  img {
    width: 100%;
    max-height: 280px;
    object-fit: cover;
    display: block;
  }

  video {
    width: 100%;
    max-height: 260px;
    display: block;
    background: #000;
  }
`;

export const MsgAudio = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 4px;

  audio {
    flex: 1;
    height: 32px;
    min-width: 0;
  }
`;

export const MsgAudioIcon = styled.div<{ saida?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${(props) => (props.saida ? "#075e54" : color.grey4)};
  color: ${color.white};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const MsgDoc = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px 4px;
  text-decoration: none;

  &:hover {
    opacity: 0.85;
  }
`;

export const MsgDocIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${color.primary};
  color: ${color.white};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const MsgDocName = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${color.almostBlack};
  max-width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const MsgDocHint = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 11px;
  color: ${color.grey4};
`;

export const MsgText = styled.div`
  padding: 10px 12px 4px;
`;

export const MsgContent = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${color.gray900};
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
`;

export const MsgInlineEditBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const MsgInlineEditInput = styled.textarea`
  width: 100%;
  min-height: 34px;
  max-height: 140px;
  border: 1px solid #86efac;
  border-radius: 8px;
  background: #ffffff;
  padding: 8px 10px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #0f172a;
  outline: none;
  resize: vertical;

  &:focus {
    border-color: #22c55e;
    box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.16);
  }
`;

export const MsgInlineEditActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

export const MsgInlineEditBtn = styled.button<{ primary?: boolean }>`
  border: 1px solid ${(props) => (props.primary ? "#22c55e" : "#cbd5e1")};
  background: ${(props) => (props.primary ? "#22c55e" : "#ffffff")};
  color: ${(props) => (props.primary ? "#ffffff" : "#334155")};
  border-radius: 8px;
  padding: 5px 9px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const MsgFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
  padding: 0 12px 6px;
`;

export const MsgTime = styled.span<{ saida?: boolean }>`
  font-family: "Inter", sans-serif;
  font-size: 11px;
  color: ${(props) => (props.saida ? color.gray500 : color.grey4)};
`;

export const MsgStatus = styled.span<{
  pending?: boolean;
  lida?: boolean;
  error?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  line-height: 1;
  color: ${(props) => {
    if (props.pending) return color.grey4;
    if (props.error) return color.error;
    if (props.lida) return "#53bdeb";
    return "#8696a0";
  }};
`;

export const MsgContextMenu = styled.div`
  position: fixed;
  z-index: 1700;
  min-width: 156px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  box-shadow: 0 16px 28px rgba(15, 23, 42, 0.2);
  overflow: hidden;
`;

export const MsgContextMenuItem = styled.button<{ danger?: boolean }>`
  width: 100%;
  border: none;
  background: #ffffff;
  color: ${(props) => (props.danger ? "#dc2626" : "#0f172a")};
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${(props) => (props.danger ? "#fff1f2" : "#f8fafc")};
  }
`;

export const NewMsgBanner = styled.button`
  position: sticky;
  top: 8px;
  align-self: center;
  z-index: 10;
  background: ${color.check};
  color: ${color.white};
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 16px;
  border-radius: 20px;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 200, 150, 0.3);
`;

export const EmptyMessages = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${color.grey4};
  font-family: "Inter", sans-serif;
  font-size: 14px;
`;

// ========== Chat Center Notice (loading / empty) ==========

const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-4px); }
`;

export const ChatCenterNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

export const ChatNoticeBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.92);
  padding: 14px 28px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

export const ChatNoticeText = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: ${color.gray500};
`;

export const ChatNoticeHint = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: ${color.grey4};
`;

export const TypingDots = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

export const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${color.grey4};
  animation: ${bounce} 1.2s ease-in-out infinite;

  &:nth-child(2) {
    animation-delay: 0.15s;
  }
  &:nth-child(3) {
    animation-delay: 0.3s;
  }
`;

// ========== Chat Input ==========

export const AttachmentPreviewBar = styled.div`
  background: #f0f2f5;
  border-top: 1px solid #dfe5ec;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 640px) {
    flex-wrap: wrap;
  }
`;

export const AttachmentPreviewMedia = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 10px;
  background: #e2e8f0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  audio {
    width: 100%;
    height: 38px;
    padding: 0 4px;
  }
`;

export const AttachmentDocIcon = styled.div`
  width: 100%;
  height: 100%;
  color: #475569;
  display: grid;
  place-items: center;
  background: #e5e7eb;
`;

export const AttachmentPreviewInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

export const AttachmentPreviewName = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const AttachmentPreviewMeta = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
`;

export const AttachmentPreviewActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const AttachmentActionButton = styled.button<{ primary?: boolean }>`
  border: 1px solid ${(props) => (props.primary ? "#25d366" : "#d5dde8")};
  background: ${(props) => (props.primary ? "#25d366" : "#ffffff")};
  color: ${(props) => (props.primary ? "#ffffff" : "#475569")};
  border-radius: 9px;
  padding: 8px 12px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ChatInputArea = styled.form`
  background: #f8fafc;
  padding: 8px 14px;
  display: flex;
  gap: 10px;
  align-items: center;
  border-top: 1px solid #e2e8f0;
  flex-shrink: 0;
`;

export const EditingMessageBar = styled.div`
  background: #e6f7eb;
  border-top: 1px solid #c7efd4;
  border-bottom: 1px solid #c7efd4;
  padding: 7px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const EditingMessageText = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: #15803d;
  font-weight: 600;
  display: block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const EditingMessageCancel = styled.button`
  border: none;
  background: transparent;
  color: #475569;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #0f172a;
  }
`;

export const IconBtn = styled.button`
  border: none;
  background: #f1f5f9;
  cursor: pointer;
  color: #64748b;
  padding: 6px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    color: #334155;
    background: #e2e8f0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ChatInputActions = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const EmojiPickerWrapper = styled.div`
  position: relative;
`;

export const EmojiPopover = styled.div`
  position: absolute;
  bottom: calc(100% + 10px);
  left: 0;
  width: 320px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.14);
  overflow: hidden;
  z-index: 20;

  @media (max-width: 640px) {
    width: min(320px, calc(100vw - 42px));
    left: -84px;
  }
`;

export const ChatInput = styled.textarea`
  flex: 1;
  border: 1px solid #dce4ef;
  border-radius: 10px;
  padding: 10px 12px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 1.35;
  color: #0f172a;
  outline: none;
  transition: border-color 0.2s;
  background: #ffffff;
  resize: none;
  min-height: 42px;
  overflow-y: hidden;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    border-color: #c9ddff;
  }
`;

export const RecordingBar = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff5f5;
  border-radius: 8px;
  padding: 10px 14px;
`;

export const RecordDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${color.error};
  animation: ${pulse} 1s infinite;
  flex-shrink: 0;
`;

export const RecordTime = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${color.error};
  font-weight: 500;
  flex: 1;
`;

export const RecordLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: ${color.error};
  opacity: 0.7;
`;

export const SendButton = styled.button<{ variant?: "danger" }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
  color: ${color.white};
  background: ${(props) =>
    props.variant === "danger" ? color.error : "#25d366"};

  &:hover {
    transform: scale(1.05);
    opacity: 0.95;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }
`;

// ========== Empty Panel ==========

export const EmptyPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #ece7de;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
        circle at 25px 25px,
        rgba(255, 255, 255, 0.15) 2px,
        transparent 2px
      ),
      radial-gradient(
        circle at 75px 75px,
        rgba(255, 255, 255, 0.15) 2px,
        transparent 2px
      );
    background-size: 100px 100px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const EmptyPanelIcon = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  color: #25d366;
  box-shadow: 0 4px 16px rgba(37, 211, 102, 0.15);
  position: relative;
  z-index: 1;
`;

export const EmptyPanelText = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: ${color.gray500};
  position: relative;
  z-index: 1;
`;

export const EmptyPanelSub = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: ${color.grey4};
  margin-top: 4px;
  position: relative;
  z-index: 1;
`;

// ========== Connection ==========

export const ConnectWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 50%, #e0f2f1 100%);
  padding: 32px;
  border-radius: 8px;
`;

export const ConnectCard = styled.div`
  max-width: 420px;
  width: 100%;
  background: ${color.white};
  border-radius: 16px;
  border: 1px solid ${color.cardBorder};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

export const ConnectStatusBar = styled.div<{ connected?: boolean }>`
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid ${color.cardBorder};
  background: ${(props) => (props.connected ? "#f0fdf4" : color.ice)};
`;

export const StatusDot = styled.div<{
  connected?: boolean;
  checking?: boolean;
}>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => {
    if (props.connected) return color.check;
    if (props.checking) return "#f59e0b";
    return color.gray300;
  }};
  ${(props) =>
    (props.connected || props.checking) &&
    css`
      animation: ${pulse} 2s infinite;
    `}
`;

export const StatusTitle = styled.div<{ connected?: boolean }>`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.connected ? color.Success : color.gray500)};
`;

export const StatusSubtitle = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: ${color.grey4};
  margin-top: 2px;
`;

export const ConnectBody = styled.div`
  padding: 32px 24px;
  text-align: center;
`;

export const ConnectIconWrapper = styled.div<{ connected?: boolean }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  background: ${(props) => (props.connected ? "#dcfce7" : color.gray100)};
  color: ${(props) => (props.connected ? color.Success : color.gray500)};
`;

export const QrContainer = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  background: #f0fdf4;
  border-radius: 12px;
  display: inline-block;

  img {
    width: 220px;
    height: 220px;
    margin: 0 auto;
    border-radius: 12px;
    border: 4px solid #25d366;
    display: block;
    box-shadow: 0 4px 16px rgba(37, 211, 102, 0.2);
  }
`;

export const QrHint = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: ${color.grey4};
  margin-top: 10px;
`;

export const ConnectBtn = styled.button`
  width: 100%;
  background: #25d366;
  color: ${color.white};
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 14px;
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);

  &:hover {
    background: #20bd5a;
    box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

export const ConnectedInfo = styled.div`
  background: #f0fdf4;
  border-radius: 8px;
  padding: 14px 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${color.Success};
`;

export const DeleteBtn = styled.button`
  width: 100%;
  border: 1px solid ${color.lightdanger};
  color: ${color.Danger};
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 14px;
  padding: 12px 24px;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  transition: background 0.2s;

  &:hover {
    background: #fff5f5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const HintText = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 11px;
  color: ${color.grey4};
  margin-top: 12px;
`;

export const Spinner = styled.span`
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
  display: inline-block;
`;

// ========== Modal ==========

export const ModalSearchSection = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${color.cardBorder};
`;

export const ModalImportSection = styled.div`
  padding: 10px 16px;
  border-bottom: 1px solid ${color.cardBorder};
`;

export const ImportBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid ${color.Primaryv3};
  color: ${color.primary};
  font-size: 13px;
  font-weight: 600;
  font-family: "Inter", sans-serif;
  padding: 8px;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${color.Primary50};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ModalBody = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const ContatoCard = styled.div`
  border: 1px solid ${color.cardBorder};
  border-radius: 8px;
  padding: 12px;
  background: ${color.ice};
`;

export const ContatoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ContatoActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

export const ContatoBtn = styled.button<{ variant?: "primary" | "default" }>`
  flex: ${(props) => (props.variant === "primary" ? 1 : "none")};
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  ${(props) =>
    props.variant === "primary"
      ? css`
          background: ${color.primary};
          color: ${color.white};
          border: none;
          &:hover {
            opacity: 0.9;
          }
        `
      : css`
          background: transparent;
          color: ${color.grey4};
          border: 1px solid ${color.cardBorder};
          &:hover {
            color: ${color.Danger};
            border-color: ${color.lightdanger};
          }
        `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ConvertForm = styled.form`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${color.cardBorder};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const FormLabel = styled.label`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: ${color.gray500};
  margin-bottom: 2px;
  display: block;
`;

export const FormInput = styled.input`
  width: 100%;
  border: 1px solid ${color.gray300};
  border-radius: 8px;
  padding: 8px 12px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: ${color.gray900};
  outline: none;

  &::placeholder {
    color: ${color.grey4};
  }

  &:focus {
    border-color: ${color.primary};
  }
`;

export const FormActions = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
`;

export const MsgTextarea = styled.textarea`
  width: 100%;
  border: 1px solid ${color.gray300};
  border-radius: 8px;
  padding: 10px 12px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${color.gray900};
  outline: none;
  resize: vertical;
  min-height: 80px;
  line-height: 1.5;

  &::placeholder {
    color: ${color.grey4};
  }

  &:focus {
    border-color: #25d366;
    box-shadow: 0 0 0 2px rgba(37, 211, 102, 0.1);
  }
`;

// ========== Send Panel ==========

export const SendPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: ${color.white};
`;

export const SendHeader = styled.div`
  padding: 16px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid ${color.cardBorder};
  background: ${color.ice};
`;

export const SendHeaderIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #25d366;
  color: ${color.white};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const SendHeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const SendHeaderTitle = styled.div`
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 15px;
  color: ${color.gray900};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SendHeaderSub = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: ${color.gray500};
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
`;

export const SendBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px;
  overflow-y: auto;
`;

export const SendForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 600px;
  width: 100%;
`;

export const SendFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const SentItem = styled.div`
  background: ${color.ice};
  border: 1px solid ${color.cardBorder};
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 6px;
`;

export const SentItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

export const SentItemLead = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${color.gray900};
`;

export const SentItemTime = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 11px;
  color: ${color.grey4};
`;

export const SentItemMsg = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: ${color.gray500};
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
`;

// ========== Envio em Massa ==========

export const MassaHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 0;
  border-bottom: 1px solid #e5e7eb;
`;

export const MassaTabs = styled.div`
  display: flex;
  gap: 0;
`;

export const MassaTab = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: none;
  color: ${(p) => (p.active ? "#25d366" : "#6b7280")};
  border-bottom: 2px solid ${(p) => (p.active ? "#25d366" : "transparent")};
  transition: all 0.15s;

  &:hover {
    color: #25d366;
  }
`;

export const MassaLayout = styled.div`
  display: flex;
  height: 100%;
  min-height: 0;
`;

export const MassaColuna = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
  min-width: 0;

  &:first-child {
    border-right: 1px solid #e5e7eb;
  }
`;

export const MassaColunaTitulo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${color.gray900};
  margin-bottom: 12px;
`;

export const MassaContatoCount = styled.span`
  margin-left: auto;
  font-size: 12px;
  font-weight: 500;
  color: #25d366;
  background: #ecfdf5;
  padding: 2px 8px;
  border-radius: 10px;
`;

export const MassaSelectAll = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 4px;
`;

export const MassaContatoLista = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }
`;

export const MassaContatoItem = styled.div<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.1s;
  background: ${(p) => (p.selected ? "#f0fdf4" : "transparent")};

  &:hover {
    background: #f9fafb;
  }
`;

// ── Seletor de modelo rápido ──

export const MassaModeloSelector = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
`;

export const MassaModeloBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: white;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #25d366;
    color: #25d366;
  }
`;

export const MassaModeloLista = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 10px;
  max-height: 180px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }
`;

export const MassaModeloItem = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.1s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f0fdf4;
  }
`;

export const MassaModeloItemTitulo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${color.gray900};
`;

export const MassaModeloItemCat = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: #25d366;
  background: #ecfdf5;
  padding: 1px 6px;
  border-radius: 8px;
`;

export const MassaModeloItemPreview = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 11px;
  color: #9ca3af;
  margin: 3px 0 0;
  line-height: 1.4;
`;

// ── Aba Modelos ──

export const MassaModelosContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  overflow: hidden;
`;

export const MassaModelosHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

export const MassaModelosBtns = styled.div`
  display: flex;
  gap: 8px;
`;

export const MassaModeloActionBtn = styled.button<{ variant?: "ghost" }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 6px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid ${(p) => (p.variant === "ghost" ? "#e5e7eb" : "#25d366")};
  background: ${(p) => (p.variant === "ghost" ? "white" : "#25d366")};
  color: ${(p) => (p.variant === "ghost" ? "#6b7280" : "white")};

  &:hover {
    opacity: 0.85;
  }
`;

export const MassaModeloForm = styled.div`
  border: 1px solid #d1fae5;
  background: #f0fdf4;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 12px;
`;

export const MassaModeloFormRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
`;

export const MassaModeloFormField = styled.div`
  display: flex;
  flex-direction: column;
`;

export const MassaModeloFormActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

export const MassaModelosLista = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }
`;

export const MassaModeloCard = styled.div<{ editing?: boolean }>`
  border: 1px solid ${(p) => (p.editing ? "#25d366" : "#e5e7eb")};
  border-radius: 8px;
  padding: 12px;
  background: white;
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover {
    border-color: #d1d5db;
  }
`;

export const MassaModeloCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

export const MassaModeloCardTitulo = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${color.gray900};
`;

export const MassaModeloCardCat = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 10px;
  font-weight: 500;
  color: #25d366;
  background: #ecfdf5;
  padding: 1px 6px;
  border-radius: 8px;
  margin-top: 2px;
  display: inline-block;
`;

export const MassaModeloCardActions = styled.div`
  display: flex;
  gap: 2px;
  opacity: 0.5;
  transition: opacity 0.15s;

  ${MassaModeloCard}:hover & {
    opacity: 1;
  }
`;

export const MassaModeloCardMsg = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: #6b7280;
  margin: 6px 0 0;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
`;

// ========== New Full Page Tabs (Envio em Massa + Histórico) ==========

export const MassPage = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #f5f7fb;
  padding: 12px;
`;

export const MassColumns = styled.div<{ singleColumn?: boolean }>`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: ${(props) => (props.singleColumn ? "1fr" : "1fr 1fr")};
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    overflow: auto;
  }
`;

export const MassColumn = styled.div`
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const MassCard = styled.div`
  border: 1px solid #e4eaf4;
  border-radius: 12px;
  background: #ffffff;
  padding: 14px;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const MassCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

export const MassCardTitle = styled.h3`
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  display: inline-flex;
  gap: 8px;
  align-items: center;
`;

export const MassCardSub = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
`;

export const MassCardSubSpaced = styled(MassCardSub)`
  margin-bottom: 10px;
`;

export const MassSearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

export const MassSaldoRow = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 12px;
`;

export const MassSearchWrapper = styled(SearchWrapper)`
  flex: 1;
  margin-bottom: 0;
`;

export const MassFilterButton = styled.button<{ $active?: boolean }>`
  height: 36px;
  min-width: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? color.primary : "#e0e0e0")};
  background: ${({ $active }) => ($active ? color.primary : color.white)};
  color: ${({ $active }) => ($active ? color.white : "#64748b")};
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0 10px;

  &:hover {
    border-color: ${color.primary};
  }
`;

export const MassFiltersCount = styled.span`
  position: absolute;
  top: -5px;
  right: -5px;
  background: ${color.error};
  min-width: 16px;
  height: 16px;
  border-radius: 100%;
  font-family: "Inter", sans-serif;
  font-size: 8px;
  line-height: 16px;
  text-align: center;
  color: ${color.white};
  padding: 0 3px;
`;

export const MassSkeletonTextBlock = styled.div`
  flex: 1;
`;

export const MassImportBox = styled.div`
  border: 1px dashed #cfd8e6;
  border-radius: 10px;
  min-height: 100px;
  display: grid;
  place-items: center;
  text-align: center;
  gap: 10px;
  padding: 12px;
  color: #64748b;
  font-family: "Inter", sans-serif;
  font-size: 14px;

  p {
    margin: 0;
  }

  label {
    border: 1px solid #d7e2ff;
    background: #eef3ff;
    color: ${color.primary};
    font-weight: 700;
    border-radius: 9px;
    padding: 8px 12px;
    cursor: pointer;
  }

  input {
    display: none;
  }
`;

export const MassToggleAll = styled.button`
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #475569;
  font-weight: 600;
  margin-bottom: 8px;
  cursor: pointer;

  input {
    accent-color: ${color.primary};
  }
`;

export const MassToggleAllHint = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #92590b;
  background: #fff7ea;
  border: 1px solid #fde2b3;
  border-radius: 999px;
  padding: 2px 8px;
`;

export const MassContactsList = styled.div`
  flex: 1;
  min-height: 220px;
  overflow: auto;
  border-top: 1px solid #edf2f7;
  padding-top: 8px;
`;

export const MassContactItem = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f8fafc;
  }

  input {
    accent-color: ${color.primary};
  }
`;

export const MassContactLeft = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
`;

export const MassContactMeta = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;

  strong {
    font-family: "Inter", sans-serif;
    font-size: 14px;
    color: #0f172a;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    font-family: "Inter", sans-serif;
    font-size: 12px;
    color: #94a3b8;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const MassTemplateBar = styled.div`
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

export const MassTemplateLabel = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  margin-bottom: 8px;
`;

export const MassNewTemplateAction = styled.button`
  border: none;
  background: transparent;
  color: ${color.primary};
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  padding: 0;

  &:hover {
    opacity: 0.85;
  }
`;

export const MassTemplateItem = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

export const MassTemplateChip = styled.button<{ active?: boolean }>`
  border: 1px solid #e2e8f0;
  background: ${(props) => (props.active ? "#edf3ff" : "#f8fafc")};
  color: ${(props) => (props.active ? color.primary : "#475569")};
  border-radius: 8px;
  padding: 6px 58px 6px 10px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: #d6e3ff;
    color: ${color.primary};
  }
`;

export const MassTemplateHoverActions = styled.div`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.16s ease;

  ${MassTemplateItem}:hover & {
    opacity: 1;
    pointer-events: auto;
  }
`;

export const MassTemplateIconBtn = styled.button<{ variant?: "danger" }>`
  width: 20px;
  height: 20px;
  border: 1px solid
    ${(props) => (props.variant === "danger" ? "#fca5a5" : "#bfdbfe")};
  background: ${(props) =>
    props.variant === "danger" ? "#fff1f2" : "#ffffff"};
  color: ${(props) => (props.variant === "danger" ? "#dc2626" : "#2563eb")};
  border-radius: 999px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

export const MassTemplateBadge = styled.span`
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(0, 128, 105, 0.12);
  color: #008069;
  padding: 1px 5px;
  border-radius: 3px;
  margin-left: 4px;
`;

export const MassHiddenSection = styled.div`
  margin-top: 8px;
`;

export const MassHiddenToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #667781;
  cursor: pointer;
  padding: 6px 0;

  &:hover {
    color: #008069;
  }
`;

export const MassHiddenArrow = styled.span<{ open?: boolean }>`
  font-size: 16px;
  line-height: 1;
  transition: transform 0.2s;
  transform: rotate(${(props) => (props.open ? "90deg" : "0deg")});
`;

export const MassHiddenList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 0 2px;
`;

export const MassHiddenItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px dashed #d1d9e7;
`;

export const MassHiddenName = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #667781;
`;

export const MassHiddenRestore = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  background: none;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #008069;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 6px;
  transition: background 0.15s;

  &:hover {
    background: rgba(0, 128, 105, 0.08);
  }
`;

export const MassTokens = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

export const MassToken = styled.button`
  border: none;
  background: #eef2f7;
  color: #64748b;
  border-radius: 999px;
  padding: 4px 9px;
  font-family: "Inter", sans-serif;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
`;

export const MassTextarea = styled.textarea`
  width: 100%;
  min-height: 160px;
  border: 1px solid #dce4ef;
  border-radius: 10px;
  padding: 12px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  resize: vertical;
  color: #0f172a;
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    border-color: #c9ddff;
  }
`;

export const MassAttachmentRow = styled.div`
  margin-top: 12px;
  border: 1px dashed #d1d9e8;
  border-radius: 9px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: space-between;
  flex-wrap: wrap;
`;

export const MassAttachmentActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export const MassAttachmentButton = styled.label`
  border: none;
  background: transparent;
  color: #475569;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;

  input {
    display: none;
  }
`;

export const MassAttachmentLibraryButton = styled.button`
  border: 1px solid #d8e2f0;
  background: #f8fbff;
  color: #2d5bff;
  border-radius: 8px;
  padding: 7px 11px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
`;

export const MassAttachmentName = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: #94a3b8;
`;

export const MassAttachmentPreviewScroll = styled.div`
  margin-top: 10px;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
  }
`;

export const MassAttachmentPreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 8px;
  align-content: start;
`;

export const MassAttachmentPreviewCard = styled.div`
  position: relative;
  border: 1px solid #dbe5f4;
  border-radius: 10px;
  background: #f8fbff;
  overflow: hidden;
`;

export const MassAttachmentPreviewThumb = styled.div`
  width: 100%;
  height: 88px;
  background: #eef3fb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5f6f89;
  font-family: "Inter", sans-serif;
  font-size: 11px;
  font-weight: 700;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const MassAttachmentPreviewMeta = styled.div`
  padding: 7px 8px;
  display: flex;
  flex-direction: column;

  strong {
    font-family: "Inter", sans-serif;
    font-size: 11px;
    color: #334155;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    margin-top: 1px;
    font-family: "Inter", sans-serif;
    font-size: 10px;
    color: #94a3b8;
  }
`;

export const MassAttachmentPreviewRemove = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  border: none;
  background: rgba(15, 23, 42, 0.65);
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
`;

export const MassSelectionFooter = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

export const MassSendButton = styled.button`
  border: none;
  background: #2d5bff;
  color: #ffffff;
  border-radius: 11px;
  padding: 11px 18px;
  font-family: "Inter", sans-serif;
  font-size: 22px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 180px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s ease, box-shadow 0.15s ease;

  &:not(:disabled):hover {
    background: #2d5bff;
    box-shadow: 0 4px 12px rgba(45, 91, 255, 0.28);
  }

  &:disabled {
    opacity: 1;
    background: #b5c2f9;
    color: #eef2ff;
    border-color: #b5c2f9;
    box-shadow: none;
    cursor: not-allowed;
  }
`;

export const MassSecondaryButton = styled.button`
  border: 1px solid #d6deea;
  background: #ffffff;
  color: #64748b;
  border-radius: 10px;
  padding: 10px 14px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

export const MassFooterActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const MassAgendarButton = styled.button`
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  border-radius: 11px;
  padding: 11px 18px;
  font-family: "Inter", sans-serif;
  font-size: 22px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 140px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:not(:disabled):hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  &:disabled {
    color: #94a3b8;
    background: #ffffff;
    border-color: #e2e8f0;
    cursor: not-allowed;
  }
`;

/* ===== Modal Agendar Disparo ===== */
export const AgendarModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #eef2f7;
`;

export const AgendarModalTitle = styled.h3`
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1f2a44;
`;

export const AgendarModalTitleIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: #eef3ff;
  color: #2d5bff;
`;

export const AgendarModalBody = styled.div`
  padding: 20px;
`;

export const AgendarModalLabel = styled.span`
  display: block;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 8px;
`;

export const AgendarModalLabelSpaced = styled(AgendarModalLabel)`
  margin-top: 18px;
`;

export const AgendarPickerWrapper = styled.div`
  width: 100%;
`;

export const AgendarPickerInput = styled(Input)`
  width: 100%;

  .MuiOutlinedInput-root {
    border-radius: 9px;
    background: #f8fafc;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    color: #334155;

    fieldset {
      border-color: #e2e8f0;
    }

    &:hover fieldset {
      border-color: #cbd5e1;
    }

    &.Mui-focused fieldset {
      border-color: #c4d3ff;
    }
  }

  .MuiOutlinedInput-input {
    padding: 11px 12px;
    font-family: "Inter", sans-serif;
    color: #334155;
  }
`;

export const AgendarModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid #eef2f7;
  background: #fbfcfe;
`;

export const AgendarConfirmButton = styled.button`
  border: none;
  background: #2d5bff;
  color: #ffffff;
  border-radius: 10px;
  padding: 10px 20px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease;

  &:not(:disabled):hover {
    box-shadow: 0 4px 12px rgba(45, 91, 255, 0.28);
  }

  &:disabled {
    background: #b5c2f9;
    color: #eef2ff;
    cursor: not-allowed;
  }
`;

/* ===== Modal Aviso Importante (agendamento) ===== */
export const AvisoAgendamentoBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 28px 26px;
`;

export const AvisoAgendamentoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fff3e6;
  color: #f5871f;
  margin-bottom: 16px;
`;

export const AvisoAgendamentoTitle = styled.h3`
  margin: 0 0 10px;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1f2a44;
`;

export const AvisoAgendamentoText = styled.p`
  margin: 0 0 22px;
  font-family: "Inter", sans-serif;
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.5;
  color: #64748b;
  max-width: 320px;

  strong {
    font-weight: 700;
    color: #475569;
  }
`;

export const AvisoAgendamentoButton = styled.button`
  width: 100%;
  border: none;
  background: #2d5bff;
  color: #ffffff;
  border-radius: 11px;
  padding: 13px 18px;
  font-family: "Inter", sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease;

  &:not(:disabled):hover {
    box-shadow: 0 4px 14px rgba(45, 91, 255, 0.3);
  }

  &:disabled {
    background: #b5c2f9;
    color: #eef2ff;
    cursor: not-allowed;
  }
`;

export const HistoryPanel = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 12px;
  background: #f5f7fb;
`;

export const HistoryHeader = styled.div`
  border: 1px solid #e4eaf4;
  border-radius: 12px;
  background: #ffffff;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const HistoryTitle = styled.h3`
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 22px;
  color: #0f172a;
  font-weight: 700;
`;

export const HistorySearch = styled.div`
  min-width: 260px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  padding: 8px 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  input {
    border: none;
    background: transparent;
    outline: none;
    font-family: "Inter", sans-serif;
    font-size: 13px;
    color: #334155;
    flex: 1;
  }
`;

export const HistoryTableWrap = styled.div`
  flex: 1;
  min-height: 0;
  margin-top: 12px;
  background: #ffffff;
  border: 1px solid #e4eaf4;
  border-radius: 12px;
  overflow: auto;
`;

export const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;

  /* Zebra: linhas alternadas — branca / azul bem sutil. */
  tbody tr:nth-child(even) {
    background: #f6f8fc;
  }

  tbody tr:hover {
    background: #eef3fb;
  }
`;

export const HistoryHeadCell = styled.th`
  position: sticky;
  top: 0;
  z-index: 2;
  text-align: left;
  font-family: "Inter", sans-serif;
  font-size: 11px;
  color: #64748b;
  font-weight: 700;
  padding: 12px 16px;
  background: #ffffff;
  /* border-collapse remove a borda em células sticky; usamos box-shadow. */
  box-shadow: inset 0 -1px 0 #e7edf7;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

export const HistoryCell = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #eef2f7;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #334155;
  max-width: 360px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.lead {
    color: #0f172a;
    font-weight: 700;
  }
`;

export const HistoryTypeBadge = styled.span<{
  type: "INDIVIDUAL" | "CAMPANHA";
}>`
  display: inline-flex;
  align-items: center;
  padding: 3px 7px;
  border-radius: 999px;
  font-family: "Inter", sans-serif;
  font-size: 10px;
  font-weight: 700;
  background: ${(props) => (props.type === "CAMPANHA" ? "#f3e8ff" : "#dbeafe")};
  color: ${(props) => (props.type === "CAMPANHA" ? "#9333ea" : "#2563eb")};
`;

export const HistoryStatus = styled.span<{
  status: "PENDENTE" | "ENVIADA" | "ENTREGUE" | "LIDA" | "ERRO";
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${(props) => {
    if (props.status === "LIDA") return "#2563eb";
    if (props.status === "ENTREGUE") return "#16a34a";
    if (props.status === "ERRO") return "#dc2626";
    if (props.status === "PENDENTE") return "#d97706";
    return "#64748b";
  }};
`;

export const HistoryEmpty = styled.div`
  padding: 24px;
  text-align: center;
  font-family: "Inter", sans-serif;
  color: #94a3b8;
  font-size: 13px;
`;

// ── Histórico: filtros (data + status) ──────────────────────────────────────
export const HistoryFilters = styled.div`
  margin: 12px 0 0;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e4eaf4;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
`;

export const HistoryFiltersTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 12px;

  svg {
    color: #2d5bff;
  }
`;

export const HistoryFiltersRow = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 16px;

  /* Normaliza o label do SelectG (Status) para casar com o "De/Até" do
     DatePickerRangeG: mesma fonte, tamanho e margem inferior. */
  label {
    font-family: Inter-Medium, "Inter", sans-serif;
    font-size: 14px;
    line-height: 150%;
    color: #333333;
    margin-bottom: 5px;
  }

  /* Iguala a altura do controle do SelectG aos inputs de data (44px). */
  .MuiOutlinedInput-root {
    height: 44px;
    border-radius: 8px;
  }
`;

export const HistoryFilterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

// Mensagem clicável que abre os detalhes do envio
export const HistoryMessageText = styled.button`
  border: none;
  background: none;
  padding: 0;
  text-align: left;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #334155;
  cursor: pointer;
  display: block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: #2d5bff;
    text-decoration: underline;
  }
`;

// Ícone/badge de erro na linha
export const HistoryErrorBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #dc2626;
  cursor: help;
`;

export const HistoryRowAction = styled.button`
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 8px;
  padding: 4px 10px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #2d5bff;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #eef2ff;
  }
`;

// ── Modal de detalhes do envio (destinatários + erro) ───────────────────────
export const HistoryDetailBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
  padding: 24px;
`;

export const HistoryDetailPanel = styled.div`
  width: min(640px, 96vw);
  max-height: 86vh;
  background: #ffffff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
`;

export const HistoryDetailHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid #e8edf5;

  h4 {
    margin: 0 0 4px;
    font-family: "Inter", sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
  }

  p {
    margin: 0;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    color: #64748b;
  }

  button {
    border: none;
    background: none;
    cursor: pointer;
    color: #64748b;
    display: inline-flex;
    padding: 4px;
    border-radius: 6px;

    &:hover {
      background: #f1f5f9;
      color: #0f172a;
    }
  }
`;

export const HistoryDetailBody = styled.div`
  padding: 16px 18px;
  overflow-y: auto;
`;

export const HistoryDetailErrorBox = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 16px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #b91c1c;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const HistoryDetailSectionTitle = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 8px;
`;

export const HistoryRecipientRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

export const HistoryRecipientInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  strong {
    font-family: "Inter", sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    font-family: "Inter", sans-serif;
    font-size: 12px;
    color: #64748b;
  }

  small {
    font-family: "Inter", sans-serif;
    font-size: 11px;
    color: #b91c1c;
    margin-top: 2px;
    word-break: break-word;
    white-space: normal;
  }
`;

// ── Histórico redesenhado: situação, contadores, filtros inline e modais ────

// Badge de situação do envio (Agendado / Em andamento / Finalizado).
export const HistorySituacaoBadge = styled.span<{
  situacao: "AGENDADO" | "ENVIADO" | "PENDENTE" | "ERRO";
}>`
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => {
    if (props.situacao === "AGENDADO") return "#ea580c";
    if (props.situacao === "PENDENTE") return "#b45309";
    if (props.situacao === "ERRO") return "#dc2626";
    return "#16a34a";
  }};
`;

// Célula numérica clicável (Enviado / Pendente / Erro) — vira pílula quando > 0.
export const HistoryCountPill = styled.button<{
  tone: "enviado" | "pendente" | "erro";
  $active?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 4px 10px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: ${(props) => (props.$active ? "pointer" : "default")};
  background: ${(props) => {
    if (!props.$active) return "transparent";
    if (props.tone === "enviado") return "#dcfce7";
    if (props.tone === "pendente") return "#fef9c3";
    return "#fee2e2";
  }};
  border-color: ${(props) => {
    if (!props.$active) return "transparent";
    if (props.tone === "enviado") return "#bbf7d0";
    if (props.tone === "pendente") return "#fde68a";
    return "#fecaca";
  }};
  color: ${(props) => {
    if (!props.$active) return "#94a3b8";
    if (props.tone === "enviado") return "#16a34a";
    if (props.tone === "pendente") return "#b45309";
    return "#dc2626";
  }};

  svg {
    flex-shrink: 0;
  }

  &:hover {
    filter: ${(props) => (props.$active ? "brightness(0.97)" : "none")};
  }
`;

// Total de contatos (número em destaque).
export const HistoryTotalValue = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
`;

// Botão "ver" (com ícone de olho) da coluna Mensagem.
export const HistoryVerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 8px;
  padding: 5px 12px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: #cbd5e1;
    color: #334155;
    background: #f8fafc;
  }
`;

// Barra de filtros inline no cabeçalho (Data de / Até / Status).
export const HistoryHeaderFilters = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;

  label {
    font-family: "Inter", sans-serif;
    font-size: 12px;
    color: #64748b;
    margin-bottom: 5px;
    white-space: nowrap;
  }

  /* Reduz a altura dos campos (data + status) só nesta barra de filtros. */
  .MuiOutlinedInput-root {
    height: 36px;
    border-radius: 8px;
    font-size: 13px;
  }

  .MuiInputBase-adornedEnd {
    height: 36px;
  }

  .MuiOutlinedInput-input {
    padding-top: 6.5px;
    padding-bottom: 6.5px;
    font-size: 13px;
  }
`;

export const HistoryHeaderFilterField = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const HistoryClearFiltersButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    border-color: #cbd5e1;
    color: #334155;
    background: #f8fafc;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ── Modal de log de contatos (Entregues / Pendentes / Falhas) ───────────────
export const HistoryLogModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;

  h4 {
    margin: 0;
    font-family: "Inter", sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
  }

  button {
    border: none;
    background: none;
    cursor: pointer;
    color: #94a3b8;
    display: inline-flex;
    padding: 4px;
    border-radius: 6px;

    &:hover {
      background: #f1f5f9;
      color: #0f172a;
    }
  }
`;

export const HistoryLogTotal = styled.div`
  padding: 0 20px 12px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #64748b;

  strong {
    color: #16a34a;
    font-weight: 700;
  }

  &[data-tone="pendente"] strong {
    color: #b45309;
  }
  &[data-tone="erro"] strong {
    color: #dc2626;
  }
`;

export const HistoryLogSubHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 20px 12px;
  flex-wrap: wrap;
`;

export const HistoryLogSectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  svg {
    color: #94a3b8;
  }
`;

export const HistoryLogSearch = styled.div`
  min-width: 220px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  padding: 7px 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  input {
    border: none;
    background: transparent;
    outline: none;
    font-family: "Inter", sans-serif;
    font-size: 13px;
    color: #334155;
    flex: 1;
  }
`;

export const HistoryLogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  padding: 0 20px 20px;
  overflow-y: auto;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

export const HistoryLogCard = styled.div<{
  tone: "enviado" | "pendente" | "erro";
}>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid
    ${(props) => {
      if (props.tone === "enviado") return "#bbf7d0";
      if (props.tone === "pendente") return "#fde68a";
      return "#fecaca";
    }};
  background: ${(props) => {
    if (props.tone === "enviado") return "#f0fdf4";
    if (props.tone === "pendente") return "#fefce8";
    return "#fef2f2";
  }};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: ${(props) => {
      if (props.tone === "enviado") return "#16a34a";
      if (props.tone === "pendente") return "#b45309";
      return "#dc2626";
    }};
  }
`;

export const HistoryLogCardInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  strong {
    font-family: "Inter", sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    font-family: "Inter", sans-serif;
    font-size: 12px;
    color: #64748b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

// ── Modal de detalhes: linhas de meta (Tipo / Data / Total) ─────────────────
export const HistoryMetaRow = styled.div`
  display: flex;
  gap: 12px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  margin-bottom: 8px;

  span:first-child {
    color: #64748b;
    min-width: 120px;
  }

  span:last-child {
    color: #0f172a;
    font-weight: 700;
  }
`;

// ── Modal de arquivos: chips de arquivo ─────────────────────────────────────
export const HistoryFileChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

export const HistoryFileChip = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #334155;
  text-decoration: none;
  max-width: 100%;
  cursor: pointer;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  svg {
    flex-shrink: 0;
    color: #64748b;
  }

  &:hover {
    border-color: #cbd5e1;
    background: #f1f5f9;
  }
`;

// ── Lightbox de imagem (visualização in-page dos anexos) ────────────────────
export const HistoryImageLightbox = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100010;
  padding: 32px;

  img {
    max-width: 92vw;
    max-height: 88vh;
    border-radius: 8px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    object-fit: contain;
  }
`;

export const HistoryImageLightboxClose = styled.button`
  position: absolute;
  top: 20px;
  right: 24px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
  border-radius: 999px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }
`;

export const MassTemplateModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 16px;
  border-bottom: 1px solid #e8edf5;
  background: #ffffff;
`;

export const MassTemplateModalTitle = styled.h3`
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 28px;
  color: #1e293b;
  font-weight: 700;
`;

export const MassTemplateModalBody = styled.div`
  padding: 18px 16px;
  background: #ffffff;
`;

export const MassTemplateModalLabel = styled.label`
  display: block;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  color: #64748b;
  font-weight: 700;
  margin-bottom: 8px;
`;

export const MassTemplateModalLabelSpaced = styled(MassTemplateModalLabel)`
  margin-top: 16px;
`;

export const MassTemplateModalInput = styled.input`
  width: 100%;
  border: 1px solid #dce4ef;
  border-radius: 10px;
  height: 44px;
  padding: 0 12px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  outline: none;
  color: #0f172a;
  background: #f8fafc;

  &::placeholder {
    color: #94a3b8;
  }
`;

export const MassTemplateModalTextarea = styled.textarea`
  width: 100%;
  min-height: 160px;
  border: 1px solid #dce4ef;
  border-radius: 10px;
  padding: 12px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  resize: vertical;
  outline: none;
  color: #0f172a;
  background: #f8fafc;

  &::placeholder {
    color: #94a3b8;
  }
`;

export const MassTemplateModalFooter = styled.div`
  padding: 14px 16px;
  border-top: 1px solid #e8edf5;
  background: #f8fafc;
  display: flex;
  justify-content: flex-end;
  gap: 10px;

  ${MassSendButton} {
    min-width: 160px;
    height: 44px;
    font-size: 16px;
    border-radius: 12px;
  }
`;

export const MassAssetModalHeader = styled.div`
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 2px 8px;
  align-items: center;
  padding: 18px 16px 14px;
  border-bottom: 1px solid #e8edf5;
  background: #ffffff;
`;

export const MassAssetModalTitle = styled.h3`
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  color: #1e293b;
  font-weight: 700;
  line-height: 1.2;
`;

export const MassAssetModalSub = styled.span`
  grid-column: 1;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #94a3b8;
  font-weight: 600;
`;

export const MassAssetModalBody = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr;
  /* flex:1 + min-height:0 permitem que o corpo encolha dentro do paper
     (flex column) em telas baixas, mantendo o rodapé sempre visível. */
  flex: 1;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    /* Em telas estreitas as duas seções empilham; permite rolagem do corpo
       inteiro para não empurrar/cortar o rodapé. */
    overflow-y: auto;
  }
`;

export const MassAssetSidebar = styled.div`
  border-right: 1px solid #e8edf5;
  padding: 16px 12px;
  min-height: 0;
  display: flex;
  flex-direction: column;

  /* O campo de busca (MassSearchWrapper tem flex:1) não pode crescer na
     vertical aqui — só a lista abaixo cresce/rola. */
  > *:first-child {
    flex: 0 0 auto;
    margin-bottom: 10px;
  }

  @media (max-width: 900px) {
    border-right: none;
    border-bottom: 1px solid #e8edf5;
  }
`;

export const MassAssetEmpList = styled.div`
  flex: 1;
  min-height: 0;
  max-height: 430px;
  overflow: auto;
  padding-right: 3px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (max-width: 900px) {
    max-height: 220px;
  }
`;

export const MassAssetEmpItem = styled.button<{ active?: boolean }>`
  width: 100%;
  border: 1px solid ${(props) => (props.active ? "#d5deff" : "transparent")};
  background: ${(props) => (props.active ? "#eef3ff" : "transparent")};
  color: ${(props) => (props.active ? "#2d5bff" : "#334155")};
  border-radius: 10px;
  padding: 12px 10px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;

  .arrow {
    font-size: 22px;
    line-height: 1;
    opacity: 0.7;
  }
`;

export const MassAssetContent = styled.div`
  padding: 16px;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const MassAssetContentTitle = styled.h4`
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 30px;
  font-weight: 700;
  color: #1e293b;
`;

export const MassAssetContentSub = styled.span`
  margin-top: 4px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
`;

export const MassAssetSelectAll = styled.button`
  margin-top: 12px;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #475569;
  font-weight: 600;
  cursor: pointer;

  input {
    accent-color: #2d5bff;
  }
`;

export const MassAssetFilesList = styled.div`
  margin-top: 16px;
  border-top: 1px solid #e8edf5;
  /* Ocupa a altura restante do corpo e rola internamente, em vez de altura
     fixa — assim o rodapé nunca é empurrado para fora em telas baixas. */
  flex: 1;
  min-height: 0;
  max-height: 410px;
  overflow: auto;

  @media (max-width: 900px) {
    max-height: none;
  }
`;

export const MassAssetFileRow = styled.button<{ active?: boolean }>`
  width: 100%;
  border: none;
  border-bottom: 1px solid #eef3fb;
  background: ${(props) => (props.active ? "#f3f7ff" : "#ffffff")};
  padding: 12px 10px;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  cursor: pointer;
`;

export const MassAssetFileIcon = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #edf2f9;
  color: #5b6b87;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 800;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const MassAssetFileMeta = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;

  strong {
    font-family: "Inter", sans-serif;
    font-size: 16px;
    color: #334155;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    margin-top: 2px;
    font-family: "Inter", sans-serif;
    font-size: 13px;
    color: #94a3b8;
    font-weight: 600;
  }
`;

export const MassAssetFileCheck = styled.span<{ checked?: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: 2px solid ${(props) => (props.checked ? "#2d5bff" : "#d6e0ef")};
  background: ${(props) => (props.checked ? "#2d5bff" : "transparent")};
  box-shadow: ${(props) =>
    props.checked ? "inset 0 0 0 4px #ffffff" : "none"};
  flex-shrink: 0;
`;

export const MassAssetModalFooter = styled.div`
  flex-shrink: 0;
  padding: 12px 16px 14px;
  border-top: 1px solid #e8edf5;
  background: #ffffff;
  display: flex;
  justify-content: flex-end;
  gap: 10px;

  ${MassSendButton} {
    min-width: 150px;
    font-size: 16px;
    border-radius: 10px;
    padding: 10px 14px;
  }
`;

export const MassAssetEmpty = styled.div`
  padding: 14px 4px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  color: #94a3b8;
`;

// ========== Disconnect Modal ==========

export const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(11, 20, 26, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ConfirmCard = styled.div`
  background: ${color.white};
  border-radius: 6px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 4px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
`;

export const ConfirmBody = styled.div`
  padding: 24px 28px;
`;

export const ConfirmTitle = styled.h3`
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #111b21;
  margin: 0 0 8px;
`;

export const ConfirmText = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: #667781;
  line-height: 1.5;
  margin: 0;
`;

export const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 28px 20px;
`;

export const ConfirmBtnCancel = styled.button`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #008069;
  background: transparent;
  border: none;
  border-radius: 6px;
  padding: 10px 24px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #f0f2f5;
  }
`;

export const ConfirmBtnDanger = styled.button`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${color.white};
  background: #ea4335;
  border: none;
  border-radius: 6px;
  padding: 10px 24px;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// ========== Sidebar Menu Button ==========

export const SidebarMenuBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #54656f;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
`;

export const SidebarDropdown = styled.div`
  position: absolute;
  right: 16px;
  top: 48px;
  z-index: 100;
  background: ${color.white};
  border-radius: 6px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.16);
  min-width: 200px;
  padding: 6px 0;
  animation: slideIn 0.12s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-4px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

export const SidebarDropdownItem = styled.button<{ danger?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  border: none;
  background: transparent;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${(props) => (props.danger ? "#ea4335" : "#111b21")};
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f0f2f5;
  }
`;
